import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

const RegisterPage = () => {
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    password: '',
    confirmPassword: '',
    invitationCode: '',
  });
  const [error, setError] = useState('');
  const [passwordErrors, setPasswordErrors] = useState([]);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { register, authError, clearAuthError } = useAuth();

  // Clear auth error when component mounts or unmounts
  useEffect(() => {
    clearAuthError();
    return () => clearAuthError();
  }, [clearAuthError]);

  // Update local error state if global auth error changes
  useEffect(() => {
    if (authError) {
      setError(authError);
    }
  }, [authError]);

  const validatePassword = (password) => {
    const errors = [];
    if (password.length < 6) {
      errors.push('Şifre en az 6 karakter olmalıdır');
    }
    
    // Require at least 2 of the following: uppercase, digit, special character
    let strengthCount = 0;
    if (/[A-Z]/.test(password)) strengthCount++;
    if (/[0-9]/.test(password)) strengthCount++;
    if (/[!@#$%^&*(),.?":{}|<>]/.test(password)) strengthCount++;
    
    if (strengthCount < 2 && password.length > 0) {
      errors.push('Şifre aşağıdakilerden en az ikisini içermelidir: büyük harf, rakam, özel karakter');
    }
    
    return errors;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    
    setFormData({
      ...formData,
      [name]: value,
    });

    // Validate password in real-time when it changes
    if (name === 'password') {
      setPasswordErrors(validatePassword(value));
    }

    // Clear error when user starts typing
    if (error) {
      setError('');
      clearAuthError();
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // Validate password before submission
    const errors = validatePassword(formData.password);
    if (errors.length > 0) {
      setPasswordErrors(errors);
      return;
    }

    if (formData.password !== formData.confirmPassword) {
      setError('Şifreler eşleşmiyor');
      return;
    }

    if (!formData.invitationCode) {
      setError('Davet kodu gereklidir');
      return;
    }

    setLoading(true);

    try {
      await register(
        formData.username, 
        formData.email, 
        formData.password, 
        formData.invitationCode
      );
      navigate('/dashboard');
    } catch (error) {
      // Error is handled by the AuthContext and will be picked up by the useEffect
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-gray-900 to-gray-800">
      <div className="max-w-md w-full space-y-8 p-8 bg-gray-800/80 backdrop-blur-sm rounded-xl shadow-xl border border-gray-700">
        <div className="text-center">
          <h2 className="mt-6 text-3xl font-extrabold text-white">
            Yeni Hesap Oluşturun
          </h2>
          <p className="mt-2 text-sm text-gray-400">
            Zaman çizelgelerinizi oluşturmaya başlayın
          </p>
        </div>
        <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
          {error && (
            <div className="bg-red-500/90 text-white p-3 rounded-lg text-center font-medium shadow-md flex items-center justify-center">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2 flex-shrink-0" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
              <span>{error}</span>
            </div>
          )}
          <div className="space-y-4">
            <div>
              <label htmlFor="username" className="block text-sm font-medium text-gray-300 mb-1">
                Kullanıcı Adı
              </label>
              <input
                id="username"
                name="username"
                type="text"
                required
                value={formData.username}
                onChange={handleChange}
                className="appearance-none block w-full px-4 py-3 border border-gray-600 rounded-lg placeholder-gray-500 text-white bg-gray-700/70 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition duration-150"
                placeholder="kullaniciadi"
              />
            </div>
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-300 mb-1">
                E-posta Adresi
              </label>
              <input
                id="email"
                name="email"
                type="email"
                required
                value={formData.email}
                onChange={handleChange}
                className="appearance-none block w-full px-4 py-3 border border-gray-600 rounded-lg placeholder-gray-500 text-white bg-gray-700/70 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition duration-150"
                placeholder="ornek@mail.com"
              />
            </div>
            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-300 mb-1">
                Şifre
              </label>
              <input
                id="password"
                name="password"
                type="password"
                required
                value={formData.password}
                onChange={handleChange}
                className={`appearance-none block w-full px-4 py-3 border ${
                  passwordErrors.length > 0 ? 'border-yellow-500' : 'border-gray-600'
                } rounded-lg placeholder-gray-500 text-white bg-gray-700/70 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition duration-150`}
                placeholder="••••••••"
              />
              {passwordErrors.length > 0 && formData.password.length > 0 && (
                <div className="mt-2 bg-yellow-500/20 border border-yellow-600 rounded-md p-3">
                  <div className="flex items-center">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1.5 text-yellow-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <p className="text-yellow-300 text-sm font-medium">Şifre gereksinimleri:</p>
                  </div>
                  <ul className="text-yellow-300 text-xs space-y-1 list-disc pl-5 mt-1">
                    {passwordErrors.map((error, index) => (
                      <li key={index}>{error}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
            <div>
              <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-300 mb-1">
                Şifre Tekrar
              </label>
              <input
                id="confirmPassword"
                name="confirmPassword"
                type="password"
                required
                value={formData.confirmPassword}
                onChange={handleChange}
                className={`appearance-none block w-full px-4 py-3 border ${
                  formData.password !== formData.confirmPassword && formData.confirmPassword
                    ? 'border-red-500'
                    : 'border-gray-600'
                } rounded-lg placeholder-gray-500 text-white bg-gray-700/70 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition duration-150`}
                placeholder="••••••••"
              />
              {formData.password !== formData.confirmPassword && formData.confirmPassword && (
                <p className="mt-1 text-red-400 text-xs flex items-center">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3 mr-1" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                  </svg>
                  Şifreler eşleşmiyor
                </p>
              )}
            </div>
            <div>
              <label htmlFor="invitationCode" className="block text-sm font-medium text-gray-300 mb-1">
                Davet Kodu
              </label>
              <input
                id="invitationCode"
                name="invitationCode"
                type="text"
                required
                value={formData.invitationCode}
                onChange={handleChange}
                className={`appearance-none block w-full px-4 py-3 border ${
                  error ? 'border-red-500' : 'border-gray-600'
                } rounded-lg placeholder-gray-500 text-white bg-gray-700/70 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition duration-150`}
                placeholder="Davet kodunuzu girin"
              />
            </div>
          </div>

          <div>
            <button
              type="submit"
              disabled={loading || passwordErrors.length > 0 || formData.password !== formData.confirmPassword}
              className="group relative w-full flex justify-center py-3 px-4 border border-transparent text-base font-medium rounded-lg text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition duration-150 shadow-lg"
            >
              {loading ? (
                <>
                  <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Hesap Oluşturuluyor...
                </>
              ) : 'Hesap Oluştur'}
            </button>
          </div>

          <div className="text-center">
            <Link
              to="/login"
              className="font-medium text-blue-400 hover:text-blue-300 transition duration-150"
            >
              Zaten hesabınız var mı? Giriş Yapın
            </Link>
          </div>
        </form>
      </div>
    </div>
  );
};

export default RegisterPage; 